import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

interface PatientOption {
  id: string;
  full_name: string;
  cpf?: string;
  phone?: string;
  email?: string;
}

interface PatientAutocompleteProps {
  value: PatientOption | null;
  onChange: (patient: PatientOption | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

const PatientAutocomplete: React.FC<PatientAutocompleteProps> = ({
  value,
  onChange,
  label = 'Paciente',
  size = 'small',
  required = false,
  disabled = false,
  error = false,
  helperText,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<PatientOption[]>([]);
  const [loading, setLoading] = useState(false);

  const searchPatients = useCallback(async (query: string) => {
    if (!query || query.length < 2) { setOptions([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/clinic/patients'), {
        params: { search: query, limit: 15 },
      });
      const patients = (res.data?.data || []).map((p: any) => ({
        id: p.id,
        full_name: p.full_name,
        cpf: p.cpf || '',
        phone: p.phone || p.whatsapp || '',
        email: p.email || '',
      }));
      setOptions(patients);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchPatients(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue, searchPatients]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, newVal) => onChange(newVal)}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      options={options}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(opt) => opt.full_name || ''}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      noOptionsText={inputValue.length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhum paciente encontrado'}
      loadingText="Buscando..."
      filterOptions={(x) => x}
      renderOption={(props, opt) => (
        <Box component="li" {...props} key={opt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <PersonIcon sx={{ color: '#94a3b8', fontSize: 20, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{opt.full_name}</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', gap: 1 }}>
              {opt.cpf && <span>CPF: {opt.cpf}</span>}
              {opt.phone && <span>Tel: {opt.phone}</span>}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          required={required}
          error={error}
          helperText={helperText}
          placeholder="Buscar por nome, CPF ou telefone..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default PatientAutocomplete;
